import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { tenantsService, type TenantView } from '../../../services/tenants/tenants.service';
import { useAdminTenants } from '../../../contexts/AdminEventsProvider';

type UseVatEditControllerParams = {
  tenantId: string;
  onError: (message: string) => void;
  // Fired once after the tenant VAT rate actually changes so callers can fan out a refresh
  // (all devices reload and the accountant report recomputes with the new per-tenant rate).
  onVatRateChanged?: () => void;
};

// Summary: Super-admin VAT-edit modal state + diff-only PATCH; maps tenantId string to tenant PK.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks
export function useVatEditController({ tenantId, onError, onVatRateChanged }: UseVatEditControllerParams) {
  const { tenants } = useAdminTenants();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tenant, setTenant] = useState<TenantView | null>(null);

  // Resolves tenantId string to a TenantView (PK); falls back to list() if the snapshot is empty.
  const open = useCallback(async () => {
    const fromSnapshot = tenants.find((candidate) => candidate.tenantId === tenantId) ?? null;
    if (fromSnapshot) {
      setTenant(fromSnapshot);
      setIsOpen(true);
      return;
    }
    try {
      const list = await tenantsService.list();
      const resolved = list.find((candidate) => candidate.tenantId === tenantId) ?? null;
      if (!resolved) {
        onError('עדכון המע"מ נכשל');
        return;
      }
      setTenant(resolved);
      setIsOpen(true);
    } catch (loadError: unknown) {
      onError(loadError instanceof Error ? loadError.message : 'עדכון המע"מ נכשל');
    }
  }, [tenants, tenantId, onError]);

  const close = useCallback(() => setIsOpen(false), []);

  // Diff-only submit (Rule 19): unchanged rate closes, no request; else PATCH by tenant PK.
  const submit = useCallback(
    async (target: TenantView, vatRate: number) => {
      if (vatRate === target.vatRate) {
        setIsOpen(false);
        return;
      }
      setIsSaving(true);
      try {
        await tenantsService.updateVatRate(target.id, vatRate);
        setIsOpen(false);
        // VAT is per-tenant (AGENTS.md Rule 46); broadcast so every device reloads and the
        // accountant report is recomputed with the new rate instead of a stale cached one.
        onVatRateChanged?.();
        notifications.show({ color: 'green', message: 'מע"מ עודכן בהצלחה', autoClose: 3000 });
      } catch (saveError: unknown) {
        onError(saveError instanceof Error ? saveError.message : 'עדכון המע"מ נכשל');
      } finally {
        setIsSaving(false);
      }
    },
    [onError, onVatRateChanged],
  );

  return { isOpen, isSaving, tenant, open, close, submit };
}
